import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';
import CallToAction from '../CallToAction';
import { cn } from '@/lib/utils';
import { Share2, Download, Globe, Smartphone, FileCode, Cpu, Package, Zap } from 'lucide-react';

const features = [
    {
        title: "WebAssembly-Powered Exports",
        description: "Leverage WebAssembly to compile your applications into highly optimized, near-native performance binaries that run directly in the browser.",
        icon: Cpu,
    },
    {
        title: "Zero-Server Deployment",
        description: "Bundle your entire application into a single, self-contained package that runs entirely client-side. No backend infrastructure required.",
        icon: Package,
    },
    {
        title: "Cross-Platform Compatibility",
        description: "Export your applications to run anywhere - browsers, mobile devices, or desktop environments - all from a single codebase.",
        icon: Globe,
    },
    {
        title: "Performance Optimized",
        description: "Automatic code splitting, tree shaking, and WebAssembly compilation ensure your applications run at peak performance.",
        icon: Zap,
    }
];

const ExportAndSharePage: React.FC = () => {
    return (
        <div className="min-h-screen bg-background">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            
            <main className="container mx-auto px-4 py-6 sm:py-12 flex-grow mt-16">
                <div className="mx-auto max-w-[980px]">
                    {/* Hero Section */}
                    <div className="flex flex-col items-center text-center space-y-4 mb-12">
                        <div className="rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
                            Export & Share
                        </div>
                        <h1 className={cn(
                            "text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl",
                            "bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent"
                        )}>
                            Deploy Anywhere, Run Everywhere
                        </h1>
                        <p className="mx-auto max-w-[700px] text-lg text-muted-foreground sm:text-xl">
                            Export your applications as WebAssembly-powered, client-side bundles that run with near-native performance. No servers, no infrastructure, just pure client-side execution.
                        </p>
                    </div>

                    {/* Demo Section */}
                    <div className="mb-16">
                        <div className="overflow-hidden rounded-lg border bg-background shadow-xl">
                            <div className="flex h-14 items-center justify-between border-b px-4">
                                <div className="flex items-center space-x-3">
                                    <Share2 className="h-5 w-5 text-primary" />
                                    <span className="text-sm font-medium">Export Configuration</span>
                                </div>
                            </div>
                            <div className="h-[600px] overflow-hidden">
                                <div className="p-6 overflow-y-auto h-full">
                                    <div className="space-y-6">
                                        {/* Export Options */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-4 border rounded-lg hover:border-primary/50 transition-colors cursor-pointer">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-primary/10 rounded-md">
                                                        <Smartphone className="w-5 h-5 text-primary" />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-medium">WebAssembly Bundle</h3>
                                                        <p className="text-sm text-gray-500">Compiled to WebAssembly for near-native performance</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="p-4 border rounded-lg hover:border-primary/50 transition-colors cursor-pointer">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-primary/10 rounded-md">
                                                        <Globe className="w-5 h-5 text-primary" />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-medium">Progressive Web App</h3>
                                                        <p className="text-sm text-gray-500">Offline-capable, installable web application</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="p-4 border rounded-lg hover:border-primary/50 transition-colors cursor-pointer">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-primary/10 rounded-md">
                                                        <Download className="w-5 h-5 text-primary" />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-medium">Static Assets</h3>
                                                        <p className="text-sm text-gray-500">Optimized HTML, CSS, and JavaScript bundle</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="p-4 border rounded-lg hover:border-primary/50 transition-colors cursor-pointer">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-primary/10 rounded-md">
                                                        <FileCode className="w-5 h-5 text-primary" />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-medium">Source Code</h3>
                                                        <p className="text-sm text-gray-500">Production-ready, optimized source code</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Bundle Stats */}
                                        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                            <h3 className="font-medium mb-4">Bundle Analysis</h3>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="p-4 border rounded-lg bg-white">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Package className="w-4 h-4 text-gray-500" />
                                                        <p className="text-sm text-gray-500">Bundle Size</p>
                                                    </div>
                                                    <p className="text-sm font-medium text-gray-900">2.4 MB (gzipped)</p>
                                                </div>
                                                <div className="p-4 border rounded-lg bg-white">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Cpu className="w-4 h-4 text-gray-500" />
                                                        <p className="text-sm text-gray-500">WASM Size</p>
                                                    </div>
                                                    <p className="text-sm font-medium text-gray-900">1.2 MB</p>
                                                </div>
                                                <div className="p-4 border rounded-lg bg-white">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Zap className="w-4 h-4 text-gray-500" />
                                                        <p className="text-sm text-gray-500">Load Time</p>
                                                    </div>
                                                    <p className="text-sm font-medium text-gray-900">1.8s</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Deployment Options */}
                                        <div className="mt-6">
                                            <h3 className="font-medium mb-4">Deployment Options</h3>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="p-4 border rounded-lg">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Globe className="w-4 h-4 text-gray-500" />
                                                        <span className="text-sm font-medium">Vercel</span>
                                                    </div>
                                                    <p className="text-xs text-gray-500">Edge network, instant deployments</p>
                                                </div>
                                                <div className="p-4 border rounded-lg">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Globe className="w-4 h-4 text-gray-500" />
                                                        <span className="text-sm font-medium">Netlify</span>
                                                    </div>
                                                    <p className="text-xs text-gray-500">Global CDN, edge functions</p>
                                                </div>
                                                <div className="p-4 border rounded-lg">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Globe className="w-4 h-4 text-gray-500" />
                                                        <span className="text-sm font-medium">GitHub Pages</span>
                                                    </div>
                                                    <p className="text-xs text-gray-500">Free static hosting</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Features Grid */}
                    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 mb-16">
                        {features.map((feature, i) => (
                            <div
                                key={i}
                                className="relative overflow-hidden rounded-lg border bg-background p-6"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                                    <feature.icon className="h-6 w-6 text-primary" />
                                </div>
                                <div className="mt-4 space-y-2">
                                    <h3 className="font-bold">{feature.title}</h3>
                                    <p className="text-muted-foreground text-sm">
                                        {feature.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <CallToAction />
            </main>
            <LandingFooter />
        </div>
    );
};

export default ExportAndSharePage; 